import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Requester } from '../../../models/RequesterModel';
import showToast from '../../../utils/toast';
import RequestersTable from './RequestersTable';
import RequesterForm from '../../../components/Companies/RequestersForm';
import ButtonCancelForm from '../../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../../components/Base/ConfirmAlert';
import useRequestersService from '../../../services/useRequestersService';
import useCompaniesService from '../../../services/useCompaniesService';
import { useFormErrors } from '../../../hooks/useFormErrors';
import TableTextSearch from '../../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../../components/Base/MaintainerPageShell';
import { Plus, UserCheck } from 'lucide-react';

const RequestersPage: React.FC = () => {
  const svc = useRequestersService();
  const { list: getRequesters, store: createRequester, update: updateRequester, remove: removeRequester, fetching } = svc;
  const companiesSvc = useCompaniesService();
  const { list: listCompanies } = companiesSvc;

  const [requesters, setRequesters] = useState<Requester[]>([]);
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Requester | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [requesterIdToDelete, setRequesterIdToDelete] = useState<number | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCompanies = useCallback(() => {
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    listCompanies({}, {
      onSuccess: (resp: any) => {
        const items = resp?.data.companies ?? [];
        const mapped = items.map((c: any) => ({
          value: c.id,
          label: c.name ?? c.company_name ?? 'Sin nombre',
        }));
        setCompanies(mapped);
      },
      onError: () => showToast('Error cargando empresas', 'error'),
    }, ctrl.signal);
  }, [listCompanies]);

  const loadRequesters = useCallback((search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page: 1 };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    getRequesters(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.requesters) ? data.requesters : data.items ?? [];
          setRequesters(items);
        },
        onError: (resp) => {
          if (resp?.http_status === 403) {
            showToast(
              resp?.message || 'No tienes permisos para acceder a esta sección.',
              'error'
            );
          } else {
            showToast(resp?.message ?? 'Error cargando solicitantes', 'error');
          }
        },
      },
      ctrl.signal
    );
  }, [getRequesters]);

  useEffect(() => {
    loadRequesters();
    loadCompanies();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadRequesters, loadCompanies]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => loadRequesters(value), 400);
    },
    [loadRequesters]
  );

  const openDeleteConfirmation = (id: number) => {
    setRequesterIdToDelete(id);
    setIsAlertOpen(true);
  };

  const openCreate = () => {
    svc.create({
      onSuccess: (resp: any) => {
        const list = resp?.data ?? [];
        const mapped = list.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        }));
        setCompanies(mapped);

        setEditing({
          name: '',
          first_lastname: '',
          second_lastname: '',
          last_name: '',
          phone_number: '',
          email: '',
          company: undefined,
        });

        setShowForm(true);
      },
      onError: () => showToast('Error cargando formulario', 'error'),
    });
  };

  const openEdit = (r: Requester) => {
    svc.edit(r.id!, {
      onSuccess: (resp: any) => {
        const data = resp.data ?? {};
        const requester = data.requester ?? {};
        const companiesList = data.companies ?? [];

        const normalized = {
          ...requester,
          company: requester.company_id ?? requester.company ?? '',
        };

        setEditing(normalized);
        setCompanies(companiesList.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        })));
        setShowForm(true);
      },
      onError: () => showToast('Error cargando datos del solicitante', 'error'),
    });
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);

    try {
      if (editing.id) {
        const r = await updateRequester(editing.id, {
          name: editing.name,
          first_lastname: editing.first_lastname,
          second_lastname: editing.second_lastname,
          phone_number: editing.phone_number,
          email: editing.email,
          company: editing.company,
        });
        return r;
      } else {
        const r = await createRequester({
          name: editing.name,
          first_lastname: editing.first_lastname,
          second_lastname: editing.second_lastname,
          phone_number: editing.phone_number,
          email: editing.email,
          company: editing.company,
        });
        return r;
      }
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      loadRequesters(searchTextRef.current.trim() || undefined);
      return;
    }

    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const handleConfirmDelete = () => {
    if (requesterIdToDelete === null) return;

    removeRequester(requesterIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        loadRequesters(searchTextRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setRequesterIdToDelete(null);
  };

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setRequesterIdToDelete(null);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {requesters.length}
        </span>
        solicitantes
      </span>
    );

  return (
    <MaintainerPageShell
      title="Solicitantes"
      icon={<UserCheck size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo solicitante
        </button>
      }
    >
      <RequestersTable
        data={requesters}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={requesters.length}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre, apellido o correo..."
            aria-label="Buscar solicitantes"
          />
        }
      />

      {showForm && editing && (
        <div className="companies-modal">
          <div className="companies-modal-inner">
            <h3>{editing.id ? 'Editar solicitante' : 'Crear solicitante'}</h3>
            <RequesterForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              companies={companies}
              fieldErrors={fieldErrors}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar solicitante?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </MaintainerPageShell>
  );
};

export default RequestersPage;
